import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { BTN_PRIMARY, INPUT } from "@/lib/styles";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Falta el token del enlace. Solicita un nuevo correo.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Contraseña actualizada");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex items-center justify-center p-6 font-body">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <img src="/icon-192.png" alt="Notacora" className="w-10 h-10 rounded-lg" />
          <span className="font-heading font-semibold text-2xl tracking-tight">Notacora</span>
        </div>

        {done ? (
          <div className="text-center" data-testid="reset-done">
            <ShieldCheck size={40} className="mx-auto text-emerald-500 mb-4" />
            <h1 className="font-heading font-light text-2xl tracking-tight mb-2">¡Listo!</h1>
            <p className="font-body text-sm text-slate-500 dark:text-slate-400">Tu contraseña se actualizó. Redirigiendo al inicio de sesión...</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading font-light text-2xl tracking-tight mb-1 text-center">Nueva contraseña</h1>
            <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-6 text-center">Crea tu nueva contraseña para Notacora.</p>
            <form onSubmit={submit} className="flex flex-col gap-4" data-testid="reset-form">
              <div className="relative">
                <input
                  className={`${INPUT} pr-11`}
                  type={show ? "text" : "password"}
                  placeholder="Nueva contraseña (mín. 6)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="reset-password-input"
                />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" data-testid="reset-toggle-visibility">
                  {show ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {error && (
                <p className="font-body text-xs text-rose-500 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2" data-testid="reset-error">{error}</p>
              )}
              <button type="submit" className={`${BTN_PRIMARY} w-full py-3`} disabled={loading} data-testid="reset-submit">
                {loading ? "Guardando..." : "Cambiar contraseña"}
              </button>
            </form>
            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">Volver a iniciar sesión</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
